// [template-variables] Data hooks for reviewing the VARIABLES the tagger finds
// in a template (the blanks and per-deal defaults a draft fills in). Not the
// taxonomy tags EditTagsModal edits — deal type / guild / tier are a different
// thing, and the UI never calls these "tags".
//
// Backed by apps/admin/src/routes/templateVariables.ts. Finding variables is a
// 1-2 minute run, so POST returns 202 and the proposal query polls until the
// run's result is stored.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type TemplateVariable = {
  id: string;
  start: number;
  end: number;
  current: string;
  kind: "blank" | "default";
  phase: "deterministic" | "model";
  rule: string;
  context: string;
  /** true = accepted, false = removed, null = undecided. */
  reviewed: boolean | null;
  /** [S-802] The variable this occurrence takes its value from — a company is
   *  written in many places, and the first occurrence names it. Null = this
   *  one holds its own value. */
  sameAs?: string | null;
};

export type TemplateVariableSet = {
  templateId: string;
  tenantId: string | null;
  ownerUid: string;
  status: "proposed" | "approved";
  slots: TemplateVariable[];
  editorialNotes: Array<{ start: number; end: number; text: string }>;
  rejected: Array<{
    quote: string;
    reason: "empty" | "touches_a_tag" | "not_found" | "ambiguous" | "collides";
  }>;
  sourceHash: string;
  /** The template text changed after this set was found; re-run, don't approve. */
  stale: boolean;
  createdAt: string;
  updatedAt: string;
};

export type VariableProposalJob = {
  status: "running" | "failed";
  startedAt: string;
  error: string | null;
};

export type VariableProposalResponse = {
  job: VariableProposalJob | null;
  proposal: TemplateVariableSet | null;
};

export type TemplateRef = { templateId: string; tenantId: string | null };

const POLL_MS = 3000;

function variablesPath({ templateId, tenantId }: TemplateRef): string {
  const id = encodeURIComponent(templateId);
  return tenantId === null
    ? `/layer1/templates/${id}/variables`
    : `/tenants/${encodeURIComponent(tenantId)}/templates/${id}/variables`;
}

const proposalKey = (ref: TemplateRef) =>
  ["template-variables", "proposal", ref.tenantId, ref.templateId] as const;
const approvedKey = (ref: TemplateRef) =>
  ["template-variables", "approved", ref.tenantId, ref.templateId] as const;

/**
 * Is a run still in flight? `startedAt` is what the POST returned. The job
 * record lives in one admin instance's memory, so a poll that reaches the other
 * instance sees `job: null`; in that case the stored proposal being older than
 * the run is what says "not done yet".
 */
export function isFindingVariables(
  data: VariableProposalResponse | undefined,
  startedAt: string | null
): boolean {
  if (data?.job?.status === "running") return true;
  if (data?.job?.status === "failed") return false;
  if (startedAt === null) return false;
  const proposal = data?.proposal ?? null;
  return proposal === null || Date.parse(proposal.createdAt) < Date.parse(startedAt);
}

/** My proposal for this template, polling every 3s while a run is in flight. */
export function useTemplateVariableProposal(
  ref: TemplateRef | null,
  startedAt: string | null
): UseQueryResult<VariableProposalResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ref === null ? ["template-variables", "proposal", "none"] : proposalKey(ref),
    enabled: ref !== null,
    queryFn: () => api<VariableProposalResponse>(`${variablesPath(ref!)}/proposal`),
    refetchInterval: (query) =>
      isFindingVariables(query.state.data, startedAt) ? POLL_MS : false,
    staleTime: 0,
  });
}

/** Start a run. Resolves with the running job as soon as the server accepts it. */
export function useFindTemplateVariables(
  ref: TemplateRef | null
): UseMutationResult<{ job: VariableProposalJob }, Error, void> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ job: VariableProposalJob }>(`${variablesPath(ref!)}/proposal`, {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      if (ref !== null) void qc.invalidateQueries({ queryKey: proposalKey(ref) });
    },
  });
}

export type VariableDecision = Pick<TemplateVariable, "start" | "end" | "id" | "reviewed" | "sameAs">;

/** Save renames + Accept/Remove decisions over my own proposal. */
export function useSaveTemplateVariables(
  ref: TemplateRef | null
): UseMutationResult<VariableProposalResponse, Error, VariableDecision[]> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slots) =>
      api<VariableProposalResponse>(`${variablesPath(ref!)}/proposal`, {
        method: "PUT",
        body: { slots },
      }),
    onSuccess: (data) => {
      if (ref !== null) qc.setQueryData(proposalKey(ref), data);
    },
  });
}

/** Approve my reviewed proposal as the template's variables (template:certify). */
export function useApproveTemplateVariables(
  ref: TemplateRef | null
): UseMutationResult<{ approved: TemplateVariableSet }, Error, void> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ approved: TemplateVariableSet }>(`${variablesPath(ref!)}/approve`, {
        method: "POST",
        body: {},
      }),
    onSuccess: (data) => {
      if (ref !== null) qc.setQueryData(approvedKey(ref), data);
    },
  });
}

/** The template's approved variables, or null when none are approved. */
export function useTemplateVariablesApproved(
  ref: TemplateRef | null
): UseQueryResult<{ approved: TemplateVariableSet | null }, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ref === null ? ["template-variables", "approved", "none"] : approvedKey(ref),
    enabled: ref !== null,
    queryFn: () =>
      api<{ approved: TemplateVariableSet | null }>(`${variablesPath(ref!)}/approved`),
  });
}

/** Mirror of core `toSlotId` — the server re-normalizes and is authoritative;
 *  this only lets the screen show the saved name and catch duplicates early. */
export function toVariableName(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 44);
}

/**
 * Split a slot's `context` around the slot so it can be highlighted. The
 * context is whitespace-collapsed text that starts up to 190 chars before the
 * slot, so the slot sits at or before min(start, 190) — pick the last
 * occurrence at or before that bound. Null when it cannot be located.
 */
export function splitVariableContext(
  slot: Pick<TemplateVariable, "context" | "current" | "start">
): { before: string; match: string; after: string } | null {
  const needle = slot.current.replace(/\s+/g, " ");
  if (needle.length === 0) return null;
  const at = slot.context.lastIndexOf(needle, Math.min(slot.start, 190));
  if (at < 0) return null;
  return {
    before: slot.context.slice(0, at),
    match: slot.context.slice(at, at + needle.length),
    after: slot.context.slice(at + needle.length),
  };
}
